// src/services/claimNoteService.js
// Módulo 5 del spec del cliente (2026-09-17): "Notes Log" — notas
// auditables por reclamo. Regla de negocio: una nota es editable/borrable
// solo dentro de una ventana de gracia (NOTE_EDIT_WINDOW_HOURS) desde su
// creación; pasado ese tiempo queda de solo lectura para siempre (auditoría
// legal) — se aplica DOS VECES: acá en el cliente (UX inmediata, oculta los
// botones) y en firestore.rules (la que realmente importa, server-side,
// con request.time < createdAt + duration).
//
// Solo el admin escribe notas — a diferencia de Attachments (que el
// ajustador dueño del reclamo sí puede escribir, por pedido explícito del
// dueño del proyecto), acá se sigue la regla general de ADJUSTER_ROLE.md:
// el ajustador es de solo lectura salvo excepción explícita, y no hubo
// ninguna para notas.
import { auth, db } from '../firebase/config';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

const NOTES_COLLECTION = 'claimNotes';
export const NOTE_EDIT_WINDOW_HOURS = 48;

const currentUserInfo = () => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión.');
  return { email: user.email, name: user.displayName || user.email };
};

// true mientras la nota todavía se puede editar/borrar (ventana de gracia
// no vencida) — usado por la UI para ocultar los botones antes de intentar
// la escritura (que igual la rechazaría la regla de Firestore si se vence
// justo en el medio).
export const isNoteEditable = (note) => {
  const createdAtMs = note.createdAt?.toMillis?.();
  if (!createdAtMs) return true; // optimista mientras el serverTimestamp no resuelve
  return Date.now() - createdAtMs < NOTE_EDIT_WINDOW_HOURS * 60 * 60 * 1000;
};

export const getClaimNotes = async (claimRecordId) => {
  const snapshot = await getDocs(
    query(collection(db, NOTES_COLLECTION), where('claimRecordId', '==', claimRecordId))
  );
  return snapshot.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
};

// claimMeta (claimNumber/clientName) se guarda denormalizado en la nota —
// mismo criterio que checks/claimRecords en claimService.js — para que el
// respaldo en "Casos y notas" (Dashboard admin) pueda mostrar a qué reclamo
// pertenece sin tener que resolver el claimRecordId contra otra colección.
export const createClaimNote = async (claimRecordId, text, claimMeta = {}) => {
  const trimmed = (text || '').trim();
  if (!trimmed) throw new Error('La nota no puede estar vacía.');
  const { email, name } = currentUserInfo();
  const { claimNumber = '', clientName = '' } = claimMeta;
  const docRef = await addDoc(collection(db, NOTES_COLLECTION), {
    claimRecordId,
    claimNumber,
    clientName,
    text: trimmed,
    createdByEmail: email,
    createdByName: name,
    createdAt: serverTimestamp(),
    edited: false
  });
  return { id: docRef.id, claimRecordId, claimNumber, clientName, text: trimmed, createdByEmail: email, createdByName: name, createdAt: Timestamp.now(), edited: false };
};

// Feed en vivo de TODAS las notas de reclamos, para el respaldo admin en
// "Casos y notas" (TeamWorkspace.jsx) — pedido explícito: si un P.A. hace
// una nota en un reclamo, debe verse ahí para todos los admin. Solo admin
// puede leer sin filtro (ver firestore.rules: claimNotes allow read admin
// sin where necesario).
export const subscribeAllClaimNotes = (next, onError) => {
  return onSnapshot(collection(db, NOTES_COLLECTION), snapshot => {
    next(snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)));
  }, onError);
};

// Nota automática al hacer clic en "Llamar al cliente" / "Llamar con
// Google Voice" (ClaimDetailPage.jsx) — pedido 2026-09-18. No bloquea ni
// espera la llamada en sí (esa la maneja el navegador con tel:/el deep
// link de Voice); solo deja registro de que se intentó, con hora local.
// method: 'phone' | 'google_voice'.
export const logClaimCallNote = async (claimRecordId, method, claimMeta = {}) => {
  const time = new Date().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  const label = method === 'google_voice' ? 'Google Voice' : 'teléfono';
  return createClaimNote(claimRecordId, `📞 Llamada al cliente iniciada (${label}) — ${time}`, claimMeta);
};

export const updateClaimNote = async (noteId, text) => {
  const trimmed = (text || '').trim();
  if (!trimmed) throw new Error('La nota no puede estar vacía.');
  await updateDoc(doc(db, NOTES_COLLECTION, noteId), { text: trimmed, edited: true });
};

export const deleteClaimNote = async (noteId) => {
  await deleteDoc(doc(db, NOTES_COLLECTION, noteId));
};
